'use client';

import { useLanguageRegionCode } from '@alltrails/language/hooks/useLanguageRegionCode';
import { useFormatMessage } from '@alltrails/language/hooks/useFormatMessage';
import { ReactElement } from 'react';
import Page from '@alltrails/layout/components/Page';
import { navScrollTo, HeroImage } from '@alltrails/landing-pages';
import { FormattedMessage } from 'react-intl';
import useFeatures from '@alltrails/features/hooks/useFeatures';
import type { LanguageRegionCode } from '@alltrails/core/types/LanguageRegionCode';
import hero from '../../assets/images/hero/hero-2800w.avif';
import heroMobile from '../../assets/images/hero/hero-780w.avif';
import About from '../AboutSection';
import Leadership from '../Leadership';
import MedicalAdvisor from '../MedicalAdvisor';
import Highlights from '../Highlights';
import CardGridSection from '../CardGridSection';
import OldPressCard, { OldPressReleaseCard } from '../OldPressCard';
import ContactUs from '../ContactUs';
import OurMission from '../OurMission';
import styles from './PressCms.module.scss';
import { PRESS_PAGE_STRINGS, PressNavId } from '@/utils/press.strings';
import PressReleasesSection from '@/components/PressReleasesSection';
import { PostPreview } from '@/types/shared';
import oldPressReleases from '@/content/cardData/oldPressReleases';

const shouldShowBasedOnLanguage = (card: OldPressReleaseCard, languageRegionCode: LanguageRegionCode): boolean => {
  // Always show cards with no languages specified
  // otherwise, only show them if their languages include the current languageRegionCode
  return !card.languages || card.languages.includes(languageRegionCode);
};

const PressPage = ({ pressReleases }: { pressReleases: PostPreview[] }): ReactElement => {
  const isPressCmsEnabled = useFeatures().includes('press_page_cms');
  const languageRegionCode = useLanguageRegionCode();

  const {
    formattedDefaultMessages: { HERO_SUBHEADER_TEXT, HERO_HEADER_TEXT, HERO_ALT }
  } = useFormatMessage(PRESS_PAGE_STRINGS);

  return (
    <Page disableInAppMessages hideReturningUserUpsell>
      <HeroImage
        imageData={{ src: hero, mobileSrc: heroMobile }}
        alt={HERO_ALT}
        headerText={HERO_HEADER_TEXT}
        headerSubText={HERO_SUBHEADER_TEXT}
        onArrowClick={() => navScrollTo(PressNavId.About)}
        textContainerStyles={styles.heroTextContainer}
        quality={90}
      />
      <div id={PressNavId.About}>
        <About />
      </div>

      <div id={PressNavId.Leadership}>
        <Leadership />
        <MedicalAdvisor />
      </div>

      <div id={PressNavId.Highlights}>
        <Highlights />
      </div>

      <div id={PressNavId.PressRoom}>
        {isPressCmsEnabled && <PressReleasesSection pressReleases={pressReleases}></PressReleasesSection>}
        {!isPressCmsEnabled && (
          <CardGridSection header={<FormattedMessage defaultMessage="Press room" />}>
            {oldPressReleases
              .filter(oldPressRelease => shouldShowBasedOnLanguage(oldPressRelease, languageRegionCode))
              .map(({ title, date, modalContent }: OldPressReleaseCard, index) => (
                <OldPressCard cardType="press-release" title={title} date={date} modalContent={modalContent} key={`${title}-${index}`} />
              ))}
          </CardGridSection>
        )}
      </div>

      <div id={PressNavId.Kit}>
        <ContactUs />
      </div>

      <OurMission />
    </Page>
  );
};

export default PressPage;
